import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
    Box, Card, Stack, TextField,
    Button, Grid, LinearProgress, Typography, Alert, Snackbar,
    Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
    FormControlLabel, Checkbox
} from "@mui/material";
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Refresh as RefreshIcon
} from "@mui/icons-material";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import AppHeader from '../../components/AppHeader';

const FinancialYears = ({ setIsLoggedIn }) => {
    const [loading, setLoading] = useState(false);
    const [rowData, setRowData] = useState([]);
    const [statusMsg, setStatusMsg] = useState({ open: false, msg: '', severity: 'success' });

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [formData, setFormData] = useState({
        year_name: '',
        start_date: '',
        end_date: '',
        is_active: 1
    });

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const tenant = localStorage.getItem('tenant') || '';
            const response = await axios.get(`${process.env.REACT_APP_API_URL1}/sm/payroll/financial-years`, {
                headers: { Authorization: `Bearer ${token}`, 'X-Tenant': tenant },
                withCredentials: true
            });
            if (response.data.success) setRowData(response.data.data);
        } catch (error) {
            console.error("Error fetching financial years:", error);
            setStatusMsg({ open: true, msg: 'Error fetching data', severity: 'error' });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleOpenDialog = (item = null) => {
        if (item) {
            setEditingItem(item);
            setFormData({
                year_name: item.year_name,
                start_date: item.start_date ? item.start_date.split('T')[0] : '',
                end_date: item.end_date ? item.end_date.split('T')[0] : '',
                is_active: item.is_active
            });
        } else {
            setEditingItem(null);
            setFormData({
                year_name: '',
                start_date: '',
                end_date: '',
                is_active: 1
            });
        }
        setDialogOpen(true);
    };

    const handleSave = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const tenant = localStorage.getItem('tenant') || '';
            const url = editingItem
                ? `${process.env.REACT_APP_API_URL1}/sm/payroll/financial-years/${editingItem.id}`
                : `${process.env.REACT_APP_API_URL1}/sm/payroll/financial-years`;

            const method = editingItem ? 'put' : 'post';

            const response = await axios[method](url, formData, {
                headers: { Authorization: `Bearer ${token}`, 'X-Tenant': tenant },
                withCredentials: true
            });

            if (response.data.success) {
                setStatusMsg({ open: true, msg: 'Saved successfully', severity: 'success' });
                setDialogOpen(false);
                fetchData();
            }
        } catch (error) {
            console.error("Error saving financial year:", error);
            setStatusMsg({ open: true, msg: 'Error saving data', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this financial year?")) return;
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const tenant = localStorage.getItem('tenant') || '';
            const response = await axios.delete(`${process.env.REACT_APP_API_URL1}/sm/payroll/financial-years/${id}`, {
                headers: { Authorization: `Bearer ${token}`, 'X-Tenant': tenant },
                withCredentials: true
            });
            if (response.data.success) {
                setStatusMsg({ open: true, msg: 'Deleted successfully', severity: 'success' });
                fetchData();
            }
        } catch (error) {
            console.error("Error deleting financial year:", error);
            setStatusMsg({ open: true, msg: 'Error deleting data', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const columnDefs = [
        { field: "year_name", headerName: "Year Name", width: 220 },
        { field: "start_date", headerName: "Start Date", width: 180, valueFormatter: p => p.value ? new Date(p.value).toLocaleDateString() : '' },
        { field: "end_date", headerName: "End Date", width: 180, valueFormatter: p => p.value ? new Date(p.value).toLocaleDateString() : '' },
        { field: "is_active", headerName: "Status", width: 130, cellRenderer: p => p.value ? <Chip label="Active" size="small" color="success" /> : <Chip label="Inactive" size="small" /> },
        {
            headerName: "Actions",
            width: 150,
            cellRenderer: p => (
                <Stack direction="row" spacing={1} sx={{ height: '100%', alignItems: 'center' }}>
                    <IconButton size="small" color="primary" onClick={() => handleOpenDialog(p.data)}>
                        <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(p.data.id)}>
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </Stack>
            )
        }
    ];

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f4f7fe' }}>
            <AppHeader title="Financial Years" setIsLoggedIn={setIsLoggedIn} />
            <Box sx={{ p: 3, flexGrow: 1, overflow: 'hidden' }}>
                <Card sx={{ p: 2, mb: 2, borderRadius: 3, boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)' }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6" fontWeight="bold">Master Data: Financial Years</Typography>
                        <Stack direction="row" spacing={2}>
                            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchData}>Refresh</Button>
                            <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()} sx={{ bgcolor: '#1a237e' }}>
                                Add New Year
                            </Button>
                        </Stack>
                    </Stack>
                </Card>

                {loading && <LinearProgress sx={{ mb: 1, borderRadius: 1 }} />}

                <Card sx={{ height: 'calc(100% - 130px)', borderRadius: 3, overflow: 'hidden', boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)' }}>
                    <Box className="ag-theme-quartz" sx={{ height: '100%', width: '100%' }}>
                        <AgGridReact
                            rowData={rowData}
                            columnDefs={columnDefs}
                            defaultColDef={{ sortable: true, filter: true, resizable: true }}
                        />
                    </Box>
                </Card>
            </Box>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editingItem ? 'Edit Financial Year' : 'Add New Financial Year'}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth label="Year Name (e.g. FY 2025-26)" size="small"
                                value={formData.year_name}
                                onChange={e => setFormData({ ...formData, year_name: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth label="Start Date" type="date" size="small"
                                InputLabelProps={{ shrink: true }}
                                value={formData.start_date}
                                onChange={e => setFormData({ ...formData, start_date: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth label="End Date" type="date" size="small"
                                InputLabelProps={{ shrink: true }}
                                value={formData.end_date}
                                onChange={e => setFormData({ ...formData, end_date: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={<Checkbox checked={formData.is_active === 1} onChange={e => setFormData({ ...formData, is_active: e.target.checked ? 1 : 0 })} />}
                                label="Active Year"
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setDialogOpen(false)} variant="outlined">Cancel</Button>
                    <Button onClick={handleSave} variant="contained" sx={{ bgcolor: '#1a237e' }}>Save</Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={statusMsg.open}
                autoHideDuration={6000}
                onClose={() => setStatusMsg({ ...statusMsg, open: false })}
            >
                <Alert severity={statusMsg.severity} sx={{ width: '100%' }}>{statusMsg.msg}</Alert>
            </Snackbar>
        </Box>
    );
};

// Helper for Chip
const Chip = ({ label, size, color, variant }) => {
    let bgcolor = '#e0e0e0';
    let textcolor = 'rgba(0, 0, 0, 0.87)';
    if (color === 'success') {
        bgcolor = '#2e7d3215';
        textcolor = '#2e7d32';
    }
    return (
        <Box sx={{
            display: 'inline-flex', alignItems: 'center', bgcolor, px: 1, py: 0.5, borderRadius: 1,
            fontSize: '0.75rem', fontWeight: 600, color: textcolor, border: color === 'success' ? '1px solid #2e7d3230' : '1px solid #00000010'
        }}>
            {label}
        </Box>
    );
};

export default FinancialYears;
